// AdvancedMD billing rules, frontend half.
//
// The backend decides every modifier/unit and reports its expectations in one
// context variable; this module mostly reads that data back. Absence of every new
// property (`cptModifier`, `units`, the findings variable) means the feature is
// off, and every helper here must then reproduce today's output exactly.

import { normalizList, parseStructuredString } from "./utils";
import { normalizeModifier } from "./losCodeParse";
import { MAX_UNITS, clampUnits } from "./unitsClamp";
import { isDrugCodeFor, isNonBillableList, isNotBilled } from "./notBilled";

export const BILLING_RULE_FINDINGS_VARIABLE = "billingRuleFindings";

export const CPT_MODIFIER_OPTIONS = ["25", "51", "59"];

export const VENIPUNCTURE_CODE = "36415";

// Office-procedure injection administration lines whose units the charge slip needs.
// Drug lines (cpt2codes J-codes) are matched by prefix in needsUnits.
export const INJECTION_UNIT_CODES = new Set(["96372", "90471", "90472", "95115", "95117"]);

// Units are a free count (1..MAX_UNITS); oncology drug lines routinely bill hundreds.
// Defined in the leaf module unitsClamp.js (utils.js needs it too) and re-exported here.
export { MAX_UNITS, clampUnits };

const isPlainObject = (v) => v != null && typeof v === "object" && !Array.isArray(v);

const positiveNumber = (v) => {
  if (v == null || typeof v === "boolean") return null;
  const n = Number(typeof v === "string" ? v.trim() : v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export function lineModifier(item) {
  if (!isPlainObject(item)) return "";
  return normalizeModifier(item.cptModifier);
}

export function lineUnits(item) {
  return isPlainObject(item) ? positiveNumber(item.units) : null;
}

// Mirrors the pre-existing `elem?.modifier || 1` exactly when `units` is absent.
export function displayUnits(item) {
  const units = lineUnits(item);
  if (units != null) return units;
  return (isPlainObject(item) && item.modifier) || 1;
}

export function formatCodeWithModifier(code, item) {
  const mod = lineModifier(item);
  const text = code == null ? "" : String(code);
  return mod ? `${text} - ${mod}` : text;
}

// The units property the charge slip reads for this list: cpt2codes keeps the legacy
// `modifier` (units count) preset; inOfficeProcedures uses the new `units` preset, and the
// non-billable list stores `units` like it (it never reaches the slip, but the note reads it).
const usesUnitsProperty = (type) => type === "inOfficeProcedures" || isNonBillableList(type);

export function unitsPropertyFor(type) {
  return usesUnitsProperty(type) ? "units" : "modifier";
}

export function hasExplicitUnits(type, item) {
  if (lineUnits(item) != null) return true;
  return !usesUnitsProperty(type) && isPlainObject(item) && positiveNumber(item.modifier) != null;
}

// Units dropdown write. With the feature off and no `units` key this is the original
// `{ ...c, modifier: n }`; `units` is only added where the slip will read it.
export function withUnits(item, n, { type, active = false } = {}) {
  const next = { ...item, modifier: n };
  if ("units" in (item || {}) || (active && type === "inOfficeProcedures") || isNonBillableList(type)) next.units = n;
  return next;
}

export function withCptModifier(item, mod) {
  const value = normalizeModifier(mod);
  if (value) return { ...item, cptModifier: value };
  const { cptModifier, ...rest } = item || {};
  return rest;
}

function readFindingsValue(value) {
  if (value === undefined || value === null) return null;
  if (typeof value === "string") {
    if (value.trim() === "") return null;
    const parsed = parseStructuredString(value);
    return parsed == null ? [] : readFindingsValue(parsed) || [];
  }
  if (Array.isArray(value)) return value;
  if (isPlainObject(value)) return Array.isArray(value.findings) ? value.findings : [value];
  return [];
}

const SATISFIED_STATUSES = new Set(["ok", "met", "pass", "passed", "satisfied", "resolved"]);

const textOf = (v) => (typeof v === "string" ? v.trim() : typeof v === "number" && Number.isFinite(v) ? String(v) : "");

// Returns null when the findings variable is absent (feature off), otherwise the list
// of open findings in a normalized shape. Unknown/garbled entries are dropped, never thrown.
export function parseBillingRuleFindings(value) {
  const raw = readFindingsValue(value);
  if (raw == null) return null;
  const out = [];
  for (const entry of raw) {
    if (typeof entry === "string") {
      if (entry.trim()) out.push({ key: "", message: entry.trim(), code: "", variable: "", expected: "", actual: "" });
      continue;
    }
    if (!isPlainObject(entry)) continue;
    if (entry.ok === true || entry.violation === false) continue;
    if (SATISFIED_STATUSES.has(textOf(entry.status).toLowerCase())) continue;
    const finding = {
      key: textOf(entry.rule) || textOf(entry.key) || textOf(entry.type) || textOf(entry.id),
      message: textOf(entry.message) || textOf(entry.text) || textOf(entry.description) || textOf(entry.detail),
      code: textOf(entry.code),
      variable: textOf(entry.variable) || textOf(entry.section),
      expected: textOf(entry.expected) || textOf(entry.expectedModifier),
      actual: textOf(entry.actual) || textOf(entry.actualModifier),
    };
    if (!finding.message && !(finding.code && finding.expected)) continue;
    out.push(finding);
  }
  return out;
}

export function isBillingRulesActive(contextVariables, list) {
  if (parseBillingRuleFindings(contextVariables?.["local:" + BILLING_RULE_FINDINGS_VARIABLE]) != null) return true;
  return normalizList(list).some((item) => item && ("cptModifier" in item || "units" in item));
}

function needsUnits(type, code) {
  if (type === "inOfficeProcedures") return INJECTION_UNIT_CODES.has(code);
  if (type === "cpt2codes") return isDrugCodeFor(type, code);
  return false;
}

// Flags for one code-list section. BE findings scoped to another section are left to
// that section; unscoped ones surface on the ICD-10 section (the note always has one
// under AMD). FE-side checks run only while the findings variable is present.
export function buildBillingFlags({ findingsValue, variable, list }) {
  const findings = parseBillingRuleFindings(findingsValue);
  if (findings == null) return [];

  const flags = [];
  const rows = normalizList(list);

  if (variable === "inOfficeProcedures" || variable === "cpt2codes") {
    const seen = new Set();
    for (const row of rows) {
      const code = textOf(row?.code);
      if (!code || seen.has(code)) continue;
      // Not marked seen, so a billable duplicate of the same code is still checked.
      if (code !== VENIPUNCTURE_CODE && isNotBilled(row)) continue;
      seen.add(code);
      if (code === VENIPUNCTURE_CODE) flags.push({ kind: "venipuncture", code });
      else if (needsUnits(variable, code) && !hasExplicitUnits(variable, row)) flags.push({ kind: "missingUnits", code });
    }
  }

  for (const f of findings) {
    const scoped = f.variable ? f.variable === variable : variable === "billableCodesICD10";
    if (!scoped) continue;
    flags.push({ kind: "finding", ...f });
  }

  return flags;
}
